import dayjs from 'dayjs'
import React from 'react'
import { useMemo } from 'react'
import { ColumnDef, createColumnHelper } from '@tanstack/react-table'

import {
  createNumericColumn,
  FirstColumn,
  formatNameWithAdditionalInfo,
  formatNumber,
  getPerformanceStatsColumnTranslationKey,
  GroupColumnsType,
  HookOptions,
  makeLink,
  OtherColumn,
} from './useStatsColumns'
import {
  AdGroupStatus,
  AdvertiserCostDetails,
  AdvertiserPerformanceStatsFullPartsFragment,
  AdvertiserPerformanceStatsPartsFragment,
} from '../../../__generated__/graphql'
import LinkCell from '../../../components/layout/cells/LinkCell'
import PerformanceStatsTableSelectField from '../../../components/stats/table/header/PerformanceStatsTableSelectField'
import ExpandCellWrapper from '../../../components/table/ExpandCellWrapper'
import {
  AdGroupsPerformanceStatsTableItem,
  AdCopyPerformanceStatsTableItem,
  ADGROUP_DATA_TYPE,
  BASE_STAT_DATA_TYPE,
  CAMPAIGN_DATA_TYPE,
} from '../../../interfaces/stats'
import { AdCopyPreviewCell } from '../../../pages/adgroup/adcopy/previewTable/AdCopyPreviewCell'
import AdCopyStatsStatusCell from '../../../pages/adgroup/dashboard/stats/AdCopyStatsStatusCell'
import { useAppTranslations } from '../useAppTranslations'
import { Column } from '../../../components/table/ReactTable'
import { StatusStyled } from '../../../components/StatusStyled'
import { useUserPermissions } from '../../../pages/company/userPermissions'
import { isNil } from 'lodash-es'

const adGroupColumnHelper = createColumnHelper<AdGroupsPerformanceStatsTableItem>()
const adCopyColumnHelper = createColumnHelper<AdCopyPerformanceStatsTableItem>()

const PERFORMANCE_METRICS = {
  group: 1,
  columnsName: ['impressions', 'ctr', 'clicks'],
}

const COST_METRICS = {
  group: 2,
  columnsName: ['cpc', 'cpm', 'costs'],
}

const LEAD_METRICS = {
  group: 3,
  columnsName: ['leads', 'lr', 'cpl'],
}

const ORDER_METRICS = {
  group: 4,
  columnsName: ['orders', 'or', 'cpo', 'revenue', 'roi'],
}

const ANALYTICS_METRICS = {
  group: 6,
  columnsName: [
    'pageViews',
    'bounceRate',
    'totalUsers',
    'newUsers',
    'sessions',
    'engagedSessions',
    'sessionTimeSeconds',
    'cpv',
    'engagementRate',
  ],
}

type TotalPerformanceStats =
  | (AdvertiserPerformanceStatsPartsFragment & { name: string })
  | (AdvertiserPerformanceStatsFullPartsFragment & { name: string })

const useNameColumn = <T extends string>(total: TotalPerformanceStats, options: HookOptions<T>): Column => {
  const { dateFormat, timezone, selectedDataType, selectedDataTypeChange, allDataTypes } = options
  const columnHelper = createColumnHelper<
    AdvertiserCostDetails & { name: string; id?: string; slug?: string; additionalInfo?: string }
  >()
  const adCopyColumnHelper = createColumnHelper<AdCopyPerformanceStatsTableItem>()

  return useMemo(() => {
    if (selectedDataType === ADGROUP_DATA_TYPE.ADCOPIES)
      return adCopyColumnHelper.accessor('name', {
        header: () => (
          <PerformanceStatsTableSelectField
            defaultValue={selectedDataType}
            onChange={selectedDataTypeChange}
            options={allDataTypes}
          />
        ),
        enableSorting: true,
        cell: ({ row }) => (
          <LinkCell
            value={<AdCopyPreviewCell compact item={row.original} />}
            target={makeLink({ type: selectedDataType, id: row.original?.id, row })}
            label={'adcopy'}
          />
        ),
        footer: total.name,
      }) as Column

    return columnHelper.accessor('name', {
      header: () => (
        <PerformanceStatsTableSelectField
          defaultValue={selectedDataType}
          onChange={selectedDataTypeChange}
          options={allDataTypes}
        />
      ),
      enableSorting: true,
      cell: ({ getValue, row }) => {
        const dateOrName = getValue()
        const maybeDate = dayjs(dateOrName)

        const titleContent = (() => {
          if (maybeDate.isValid() && selectedDataType === BASE_STAT_DATA_TYPE.TIMESERIES) {
            const dataWithTimezone = maybeDate.utcOffset() > 0 ? maybeDate : maybeDate.tz(timezone)

            return <>{dataWithTimezone.format(dateFormat)}</>
          } else
            return (
              <LinkCell
                value={formatNameWithAdditionalInfo(dateOrName, row.original?.additionalInfo)}
                target={makeLink({
                  type: selectedDataType,
                  id: row.original?.id ?? row.original?.slug,
                  row,
                })}
              />
            )
        })()

        return (
          <FirstColumn>
            <ExpandCellWrapper row={row}>{titleContent}</ExpandCellWrapper>
          </FirstColumn>
        )
      },
      footer: total.name,
    }) as Column
  }, [selectedDataType, total])
}

const useNumericColumnsGroup = (
  group: GroupColumnsType,
  stats: TotalPerformanceStats
): ColumnDef<AdvertiserCostDetails>[] => {
  const t = useAppTranslations()

  return useMemo(
    () =>
      group.columnsName.map((columnName) =>
        createNumericColumn<AdvertiserCostDetails>(
          columnName,
          t(getPerformanceStatsColumnTranslationKey(columnName)),
          group.group,
          stats as any
        )
      ),
    [group, stats]
  )
}

const useAdditionalColumns = (dataType: string): Column[] => {
  const t = useAppTranslations()

  return useMemo(() => {
    switch (dataType) {
      case CAMPAIGN_DATA_TYPE.ADGROUPS:
        return [
          adGroupColumnHelper.accessor('status', {
            header: () => <OtherColumn>{t('commons.status') as string}</OtherColumn>,
            cell: (info) => <StatusStyled status={info.getValue() as AdGroupStatus} />,
            footer: 'n/a',
          }),
          adGroupColumnHelper.accessor('dailyBudget', {
            header: () => <OtherColumn>{t('stats.table.dailyBudget') as string}</OtherColumn>,
            cell: (info) => (
              <>
                {!isNil(info.getValue()) && info.getValue() !== ''
                  ? formatNumber(info.getValue() as number, 'currency')
                  : t('commons.noValueYet')}
              </>
            ),
            footer: 'n/a',
            meta: {
              headerSx: {
                textAlign: 'right',
                '& .MuiTableSortLabel-root': {
                  justifyContent: 'flex-end',
                  width: '100%',
                },
              },
              cellSx: {
                fontVariantNumeric: 'tabular-nums',
                textAlign: 'right',
                whiteSpace: { md: 'nowrap', xs: 'normal' },
              },
            },
          }),
        ] as Column[]
      case ADGROUP_DATA_TYPE.ADCOPIES:
        return [
          adCopyColumnHelper.accessor('status', {
            header: () => <OtherColumn>{t('commons.status') as string}</OtherColumn>,
            cell: ({ getValue, row }) => (
              <AdCopyStatsStatusCell adCopyId={String(row.original.id)} status={getValue()} />
            ),
            footer: 'n/a',
          }),
        ] as Column[]
      default:
        return []
    }
  }, [dataType])
}

export const useAdvertiserStatsColumns = <T extends string>(
  dataType: string,
  total: TotalPerformanceStats,
  options: HookOptions<T>
): Column[] => {
  const permissions = useUserPermissions()
  const t = useAppTranslations()

  const nameColumn = useNameColumn(total, options)
  const performanceColumns = useNumericColumnsGroup(PERFORMANCE_METRICS, total)
  const costColumns = useNumericColumnsGroup(COST_METRICS, total)
  const leadColumns = useNumericColumnsGroup(LEAD_METRICS, total)
  const orderColumns = useNumericColumnsGroup(ORDER_METRICS, total)
  const additionalColumns = useAdditionalColumns(dataType)

  let additionnalColumns: ColumnDef<AdvertiserCostDetails>[] = []
  if ((permissions.superAdmin && dataType === ADGROUP_DATA_TYPE.ADCOPIES) || dataType === ADGROUP_DATA_TYPE.TIMESERIES)
    additionnalColumns = ANALYTICS_METRICS.columnsName.map((columnName) =>
      createNumericColumn<AdvertiserCostDetails>(
        columnName,
        t(`adgroup.analytics.${columnName}`),
        ANALYTICS_METRICS.group,
        total as any
      )
    )

  return [
    nameColumn,
    ...additionalColumns,
    ...performanceColumns,
    ...costColumns,
    ...leadColumns,
    ...orderColumns,
    ...additionnalColumns,
  ] as Column[]
}
